import React from 'react';
import { compose } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import classnames from 'classnames';
import Dropdown from '@wix/da-shared-react/pkg/Dropdown';
import DeviationDumbView from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import ArrowDownIcon from '@wix/da-shared-react/pkg/Icons/ArrowDown';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import SubfoldersIcon from '@wix/da-shared-react/pkg/Icons/Subfolders';
import { PapiGallection } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import { covertToFoldersWithSubfolders } from '../../../../utils/folders';
import {
  withPerspectiveContext,
  PerspectiveContextProps,
} from '@wix/da-shared-react/pkg/Context';
import { INITIAL_SCROLL_TOP } from '../../../UserWidgets/ProfileHero';
import { PERSPECTIVE_CONTAINER_ID } from '../../../App/App';

import s from './FolderDropdown.scss';
import { BiDataHook, BiEventType } from '@wix/da-shared-react/pkg/biLogger';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';

export interface Props {
  folders: PapiGallection[];
  currentlyViewedFolderId?: number;
  setSelectedFolderId: (number) => void;
  className?: string;
}

const enhance = compose(
  withPerspectiveContext,
  withTranslation()
);

export class FolderDropdown extends React.Component<
  Props & PerspectiveContextProps & WithTranslation & WithLogBiEventProps
> {
  getSelectedFolderId = () => {
    const { currentlyViewedFolderId } = this.props;
    return currentlyViewedFolderId;
  };

  render() {
    const { className } = this.props;

    return (
      <Dropdown
        className={classnames(s['root'], 'no-theme-skin', className)}
        onSelect={this.selectFolder}
        data-hook={BiDataHook.PROFILE_GALLECTION_FOLDER_CHOOSER}
      >
        {this.renderToggle()}
        {this.renderMenu()}
      </Dropdown>
    );
  }

  renderToggle() {
    const { currentlyViewedFolderId } = this.props;
    const folder = this.getFolderById(currentlyViewedFolderId);

    return (
      <Dropdown.Toggle className={s['toggle']} tabIndex="-1">
        <div className={s['selected-folder-name']}>
          {folder && folder.name}
          <span className={s['count']}>
            {shortenNumber(folder && folder.size)}
          </span>
        </div>
        <ArrowDownIcon className={s['caret']} size={IconSize.SMALL} />
      </Dropdown.Toggle>
    );
  }

  renderMenu() {
    const { folders } = this.props;
    const menuItems = [] as any[];
    const foldersWithSubfolders = covertToFoldersWithSubfolders(folders);
    foldersWithSubfolders.forEach(({ folder, subfolders }, index) => {
      menuItems.push(this.renderMenuItem(folder, index, subfolders.length > 0));
      menuItems.push(
        ...subfolders.map(subfolder => this.renderMenuItem(subfolder, index))
      );
    });
    return (
      <Dropdown.MenuWrapper
        className={s['menu-wrapper']}
        componentProps={{
          'data-hook': BiDataHook.PROFILE_GALLECTION_FOLDER_DROPDOWN,
        }}
      >
        <Dropdown.Menu className={s['menu']}>{menuItems}</Dropdown.Menu>
      </Dropdown.MenuWrapper>
    );
  }

  renderMenuItem = (folder: PapiGallection, index, hasSubfolders = false) => {
    return (
      <Dropdown.MenuItem
        key={folder.folderId}
        className={s['menu-item']}
        value={folder.folderId}
        selected={folder.folderId === this.props.currentlyViewedFolderId}
        label={folder.name}
        biData={{
          evid: BiEventType.ITEM_CLICK,
          rank: index,
          creatorid: folder.owner.useridUuid,
          typeid: gallectionTypeToResourceType(folder.type),
          itemid: folder.folderId,
        }}
      >
        {this.renderFolderInfo(folder)}
        {this.renderFolderThumb(folder, hasSubfolders)}
      </Dropdown.MenuItem>
    );
  };

  renderFolderInfo(folder: PapiGallection) {
    const { t } = this.props;
    return (
      <div
        className={classnames(
          s['info'],
          !!folder.parentId && s['subfolder-info']
        )}
      >
        <div className={s['title']}>{folder.name}</div>
        <div className={s['stats']}>
          {t('pages.gallections.folder.stats.artworks', {
            formattedCount: shortenNumber(folder.size),
            count: folder.size,
          })}
          {folder.commentCount > 0 && (
            <>
              {' | '}
              {t('pages.gallections.folder.stats.comments', {
                formattedCount: shortenNumber(folder.commentCount),
                count: folder.commentCount,
              })}
            </>
          )}
        </div>
      </div>
    );
  }

  renderFolderThumb(folder: PapiGallection, hasSubfolders) {
    const deviation = folder.thumb;
    if (!deviation) {
      return null;
    }
    const previewProps = {
      deviation,
      width: 114,
      height: 64,
    };

    return (
      <div className={s['thumb-wrapper']}>
        <DeviationDumbView {...previewProps} className={s['thumb']} />
        {hasSubfolders && (
          <SubfoldersIcon
            size={IconSize.SMALL}
            className={s['subfolders-icon']}
          />
        )}
      </div>
    );
  }

  getFolderById(id?: number) {
    if (!id) {
      return;
    }

    return this.props.folders.find(folder => folder.folderId === id);
  }

  selectFolder = (folderId, label) => {
    const scrollable = document.getElementById(PERSPECTIVE_CONTAINER_ID); // this.props.getScrollableContainer();
    if (scrollable) {
      scrollable.scrollTop = INITIAL_SCROLL_TOP;
    }
    this.props.setSelectedFolderId(folderId);
  };
}

export default enhance(withLogBiEvent(FolderDropdown)) as React.ComponentType<
  Props
>;
